import React from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography
} from '@mui/material';
import { format } from 'date-fns';
import { toDate } from '../../utils/dates';
import { formatCurrency } from '../../utils/money';
import { BRAND } from '../../theme';
import { DriverDirectory, STATUS_LABELS, driverInfoFor } from './types';

interface InvoiceDetailsDialogProps {
  open: boolean;
  invoice: any | null;
  driverDirectory: DriverDirectory;
  onClose: () => void;
}

const formatDateTime = (value: any) => {
  const date = toDate(value);
  return date ? format(date, 'MM/dd/yyyy h:mm a') : '—';
};

const text = (value: any) => {
  if (value === undefined || value === null || value === '') return '—';
  return String(value);
};

const Field: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box sx={{ flex: '1 1 calc(33% - 16px)', minWidth: 160 }}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 500 }}>
      {value}
    </Typography>
  </Box>
);

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Box sx={{ mb: 2.5 }}>
    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
      {title}
    </Typography>
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>{children}</Box>
  </Box>
);

const statusStyles = (status: string) => {
  switch (status) {
    case 'PAID':
      return { backgroundColor: BRAND.paid.background, color: BRAND.paid.text };
    case 'pay_to_driver':
      return { backgroundColor: BRAND.payToDriver.background, color: BRAND.payToDriver.text };
    default:
      return { backgroundColor: BRAND.pending.background, color: BRAND.pending.text };
  }
};

const InvoiceDetailsDialog: React.FC<InvoiceDetailsDialogProps> = ({
  open,
  invoice,
  driverDirectory,
  onClose
}) => {
  if (!invoice) return null;

  const status = invoice.status || 'pending';
  const driver = driverInfoFor(driverDirectory, invoice.driver);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        Invoice {invoice.invoiceNumber || ''}
        <Chip size="small" label={STATUS_LABELS[status] || status} sx={statusStyles(status)} />
      </DialogTitle>
      <DialogContent dividers>
        <Section title="Load">
          <Field label="Order #" value={text(invoice.orderNumber)} />
          <Field label="Load #" value={text(invoice.loadNumber)} />
          <Field label="BOL #" value={text(invoice.bolNumber)} />
          <Field label="Broker" value={text(invoice.broker)} />
          <Field label="Terminal" value={text(invoice.terminal)} />
          <Field label="Destination" value={text(invoice.destination)} />
          <Field label="Created" value={formatDateTime(invoice.createdAt)} />
          <Field label="Last Updated" value={formatDateTime(invoice.updatedAt)} />
          <Field label="Driver" value={`${driver.name}${driver.dogtag ? ` (${driver.dogtag})` : ''}`} />
        </Section>

        <Divider sx={{ mb: 2.5 }} />

        <Section title="Times">
          <Field label="Terminal Arrival" value={text(invoice.terminalArrivalTime)} />
          <Field label="Terminal Depart" value={text(invoice.terminalDepartTime)} />
          <Field label="Delivery Arrival" value={text(invoice.deliveryArrivalTime)} />
          <Field label="Delivery Depart" value={text(invoice.deliveryDepartTime)} />
        </Section>

        <Divider sx={{ mb: 2.5 }} />

        <Section title="Charges">
          <Field label="Gross" value={text(invoice.gross)} />
          <Field label="Net" value={text(invoice.net)} />
          <Field label="Rate" value={formatCurrency(invoice.rate)} />
          <Field label="Freight Revenue" value={formatCurrency(invoice.freightRev)} />
          <Field label="Fuel Surcharge %" value={text(invoice.fuelSurchargePercent)} />
          <Field label="Fuel Surcharge" value={formatCurrency(invoice.fuelSurcharge)} />
          <Field label="Demurrage" value={formatCurrency(invoice.demurrageCharge)} />
          {invoice.pumpEnabled && <Field label="Pumps" value={text(invoice.pumpCount)} />}
          {invoice.splitShareAmount ? (
            <Field label="Split Share" value={formatCurrency(invoice.splitShareAmount)} />
          ) : null}
        </Section>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
            borderRadius: 1,
            backgroundColor: '#F8FAFD'
          }}
        >
          <Typography variant="subtitle1">Total Charges</Typography>
          <Typography variant="h6" color="primary">
            {formatCurrency(invoice.totalCharges)}
          </Typography>
        </Box>

        {invoice.notes && (
          <Box sx={{ mt: 2.5 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Notes
            </Typography>
            <Typography variant="body2">{invoice.notes}</Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default InvoiceDetailsDialog;
